/**
 * Shared predicate: is this "error" actually an EXPECTED OPERATIONAL STATE
 * (plan / usage / rate limits, quota, billing, HTTP 402 / 429) rather than a
 * real bug?
 *
 * Used at every Sentry capture chokepoint (frontend beforeSend, the global
 * error handler, the backend captureException, and the worker withSentry
 * beforeSend) so these normal conditions stop creating Sentry issues.
 *
 * This file is intentionally dependency-free and byte-identical across three
 * runtimes:
 *   - lovable-migration-project/src/lib/sentry-noise.ts        (frontend)
 *   - vernot/supabase/functions/_shared/sentry-noise.ts        (Deno backend)
 *   - business-concourse-worker/src/lib/sentry-noise.ts        (worker)
 * Keep them in sync.
 *
 * CRITICAL — do NOT broaden the patterns to a bare "rate_limit". The real
 * foreign-key-violation bug on `mcp_rate_limit_buckets` must stay visible in
 * Sentry. We deliberately match only the human-readable "rate limit" (with a
 * space) and the Anthropic error token "rate_limit_error".
 */

// Message substrings (compared lowercased) that indicate an expected
// operational state rather than a bug.
const EXPECTED_MESSAGE_PATTERNS: readonly string[] = [
  "rate limit",
  "rate_limit_error",
  "too many requests",
  "quota exceeded",
  "insufficient_quota",
  "insufficient quota",
  "usage limit",
  "plan limit",
  "monthly limit",
  "daily limit",
  "hourly limit",
  "per-hour rate limit",
  "you've reached",
  "you have reached",
  "you've hit your",
  "you have hit your",
  "you've hit the",
  "upgrade your plan",
  "upgrade to ",
  "payment required",
  "billing limit",
  "subscription required",
  "trial expired",
  "past due",
  "temporarily unavailable",
  "try again in a minute",
  "try again in a moment",
  "error 429",
  "429:",
  "http 429",
  "status 429",
  "error 402",
  "402:",
];

// Error / exception `name` / `type` tokens (compared after stripping all
// non-alphanumerics and lowercasing).
const EXPECTED_NAME_TOKENS: readonly string[] = [
  "ratelimiterror",
  "insufficientquotaerror",
  "quotaexceedederror",
  "usagelimiterror",
  "planlimiterror",
];

// HTTP statuses that are operational, not exceptional.
const EXPECTED_STATUS: ReadonlySet<number> = new Set([402, 429]);

interface Collected {
  strings: string[];
  statuses: number[];
}

/**
 * Pull the few fields that could carry an operational signal out of an
 * arbitrary error / Sentry-event / Postgrest-error / HTTP-error shape. Bounded
 * (no deep recursion beyond a single cause-chain hop) so we never sweep up
 * unrelated nested data and produce a false positive.
 */
function collectFrom(input: unknown, depth: number): Collected {
  const strings: string[] = [];
  const statuses: number[] = [];
  if (input == null) return { strings, statuses };

  if (typeof input === "string") {
    if (input.length > 0) strings.push(input);
    return { strings, statuses };
  }
  if (typeof input !== "object") return { strings, statuses };

  const o = input as Record<string, unknown>;

  const pushStr = (v: unknown) => {
    if (typeof v === "string" && v.length > 0) strings.push(v);
  };
  const pushNum = (v: unknown) => {
    if (typeof v === "number" && Number.isFinite(v)) statuses.push(v);
    else if (typeof v === "string" && /^\d{3}$/.test(v.trim())) {
      statuses.push(Number(v.trim()));
    }
  };

  pushStr(o.name);
  pushStr(o.code);
  pushStr(o.reason);
  pushStr(o.type);
  pushStr(o.value);

  pushNum(o.status);
  pushNum(o.statusCode);
  pushNum(o.status_code);

  // message can be a string (Error) or an object (Sentry: { formatted }).
  if (typeof o.message === "string") {
    pushStr(o.message);
  } else if (o.message && typeof o.message === "object") {
    const m = o.message as Record<string, unknown>;
    pushStr(m.formatted);
    pushStr(m.message);
  }

  const resp = o.response;
  if (resp && typeof resp === "object") {
    const r = resp as Record<string, unknown>;
    pushNum(r.status);
    pushNum(r.statusCode);
    pushNum(r.status_code);
  }

  // Sentry event: exception.values[].{ type, value }
  const exception = o.exception;
  if (exception && typeof exception === "object") {
    const values = (exception as Record<string, unknown>).values;
    if (Array.isArray(values)) {
      for (const v of values) {
        if (v && typeof v === "object") {
          const vv = v as Record<string, unknown>;
          pushStr(vv.type);
          pushStr(vv.value);
        }
      }
    }
  }

  // Sentry event: contexts.response.status_code
  const contexts = o.contexts;
  if (contexts && typeof contexts === "object") {
    const cr = (contexts as Record<string, unknown>).response;
    if (cr && typeof cr === "object") {
      const crr = cr as Record<string, unknown>;
      pushNum(crr.status_code);
      pushNum(crr.status);
    }
  }

  // One hop down an Error cause-chain (or a wrapped error).
  if (depth < 2 && o.cause && typeof o.cause === "object") {
    const inner = collectFrom(o.cause, depth + 1);
    for (const s of inner.strings) strings.push(s);
    for (const n of inner.statuses) statuses.push(n);
  }

  return { strings, statuses };
}

function normalizeToken(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Returns true when `input` represents an expected operational state (plan /
 * usage / rate limits, quota, billing, 402 / 429) that should NOT be reported
 * to Sentry. Accepts Error, string, Sentry ErrorEvent, PostgrestError, and
 * generic HTTP-error shapes. Never throws.
 */
export function isExpectedOperationalError(input: unknown): boolean {
  const { strings, statuses } = collectFrom(input, 0);

  for (const st of statuses) {
    if (EXPECTED_STATUS.has(st)) return true;
  }

  for (const raw of strings) {
    const lower = raw.toLowerCase();
    for (const pat of EXPECTED_MESSAGE_PATTERNS) {
      if (lower.includes(pat)) return true;
    }
    const token = normalizeToken(raw);
    if (token.length > 0) {
      for (const nameTok of EXPECTED_NAME_TOKENS) {
        if (token === nameTok || token.includes(nameTok)) return true;
      }
    }
  }

  return false;
}
